import { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Package, ArrowLeft, User, MapPin, Box, MessageCircle,
  Phone, Mail, CheckCircle, Truck, Shield, AlertTriangle, Search
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { CARRIER_LOGOS } from "@/components/CarrierLogos";
import ColoniaSearch from "@/components/ColoniaSearch";

const WHATSAPP_NUMBER = "525563341691";

const ESTADOS_MEXICO = [
  "Aguascalientes", "Baja California", "Baja California Sur", "Campeche",
  "Chiapas", "Chihuahua", "Ciudad de México", "Coahuila", "Colima",
  "Durango", "Estado de México", "Guanajuato", "Guerrero", "Hidalgo",
  "Jalisco", "Michoacán", "Morelos", "Nayarit", "Nuevo León", "Oaxaca",
  "Puebla", "Querétaro", "Quintana Roo", "San Luis Potosí", "Sinaloa",
  "Sonora", "Tabasco", "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán",
  "Zacatecas"
];

const SolicitarGuia = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const quote = location.state?.quote;
  const quoteDetails = location.state?.quoteDetails;

  const [sender, setSender] = useState({
    name: "", phone: "", email: "", street: "", colonia: "",
    city: "", state: "", zip: ""
  });
  const [recipient, setRecipient] = useState({
    name: "", phone: "", email: "", street: "", colonia: "",
    city: "", state: "", zip: "", references: ""
  });
  const [pkg, setPkg] = useState({
    content: "", weight: quoteDetails?.weight || "",
    length: "", width: "", height: ""
  });

  const buildWhatsAppMessage = () => {
    let msg = `*SOLICITUD DE GUÍA - SuperPack.mx*\n\n`;

    if (quote) {
      msg += `*PAQUETERÍA ELEGIDA:*\n`;
      msg += `Paquetería: ${quote.carrier} (${quote.service_type})\n`;
      msg += `Precio: $${quote.price?.toFixed(2)} MXN\n`;
      msg += `Entrega: ${quote.delivery_days}\n\n`;
    }

    msg += `*REMITENTE (Quien envía):*\n`;
    msg += `Nombre: ${sender.name}\n`;
    msg += `Tel: ${sender.phone}\n`;
    msg += `Email: ${sender.email}\n`;
    msg += `Dirección: ${sender.street}\n`;
    msg += `Colonia: ${sender.colonia}\n`;
    msg += `Ciudad: ${sender.city}, ${sender.state}\n`;
    msg += `CP: ${sender.zip}\n\n`;

    msg += `*DESTINATARIO (Quien recibe):*\n`;
    msg += `Nombre: ${recipient.name}\n`;
    msg += `Tel: ${recipient.phone}\n`;
    msg += `Email: ${recipient.email}\n`;
    msg += `Dirección: ${recipient.street}\n`;
    msg += `Colonia: ${recipient.colonia}\n`;
    msg += `Ciudad: ${recipient.city}, ${recipient.state}\n`;
    msg += `CP: ${recipient.zip}\n`;
    if (recipient.references) msg += `Referencias: ${recipient.references}\n`;
    msg += `\n`;

    msg += `*PAQUETE:*\n`;
    msg += `Contenido: ${pkg.content}\n`;
    msg += `Peso: ${pkg.weight} kg\n`;
    msg += `Dimensiones: ${pkg.length}x${pkg.width}x${pkg.height} cm\n`;

    return msg;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sender.name || !sender.phone || !sender.zip || !sender.street || !sender.colonia || !sender.city || !sender.state) {
      toast.error("Completa todos los datos del remitente");
      return;
    }
    if (!recipient.name || !recipient.phone || !recipient.zip || !recipient.street || !recipient.colonia || !recipient.city || !recipient.state) {
      toast.error("Completa todos los datos del destinatario");
      return;
    }
    if (!pkg.content || !pkg.weight) {
      toast.error("Completa la descripción y peso del paquete");
      return;
    }

    // Save to DB
    try {
      await axios.post(`${API}/whatsapp-requests`, {
        carrier: quote?.carrier || "",
        service_type: quote?.service_type || "",
        price: quote?.price || 0,
        sender_name: sender.name, sender_phone: sender.phone, sender_email: sender.email,
        sender_address: sender.street, sender_colonia: sender.colonia,
        sender_city: sender.city, sender_state: sender.state, sender_zip: sender.zip,
        recipient_name: recipient.name, recipient_phone: recipient.phone, recipient_email: recipient.email,
        recipient_address: recipient.street, recipient_colonia: recipient.colonia,
        recipient_city: recipient.city, recipient_state: recipient.state, recipient_zip: recipient.zip,
        recipient_references: recipient.references || "",
        package_content: pkg.content, package_weight: pkg.weight,
        package_dimensions: `${pkg.length}x${pkg.width}x${pkg.height}`,
      });
    } catch { /* continue even if save fails */ }

    const msg = buildWhatsAppMessage();
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
    toast.success("Redirigiendo a WhatsApp...");
  };

  const logoUrl = quote ? CARRIER_LOGOS[quote.carrier] : null;

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
            <Package className="w-7 h-7 text-violet-600" />
            <span className="text-xl font-bold font-['Outfit']">
              <span className="text-violet-600">Super</span><span className="text-zinc-800">Pack</span>
            </span>
          </div>
          <Badge className="bg-green-100 text-green-700 border-green-300">
            <MessageCircle className="w-3.5 h-3.5 mr-1" /> Compra por WhatsApp
          </Badge>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Back button */}
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4 text-zinc-500"
          data-testid="wa-form-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver
        </Button>

        {/* Selected carrier */}
        {quote && (
          <Card className="bg-green-50 border-green-200 mb-6" data-testid="wa-selected-quote">
            <CardContent className="py-3 px-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {logoUrl && <img src={logoUrl} alt={quote.carrier} className="h-8 object-contain" />}
                <div>
                  <span className="font-bold">{quote.carrier}</span>
                  <Badge className="ml-2 bg-green-100 text-green-700 text-xs">{quote.service_type}</Badge>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-green-700">${quote.price?.toFixed(2)} MXN</p>
                <p className="text-xs text-zinc-500">{quote.delivery_days}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Remitente */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="w-5 h-5 text-violet-600" />
                Datos del remitente (quien envía)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <WhatsAppAddressForm data={sender} setData={setSender} prefix="wa-sender" />
            </CardContent>
          </Card>

          {/* Destinatario */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="w-5 h-5 text-violet-600" />
                Datos del destinatario (quien recibe)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <WhatsAppAddressForm data={recipient} setData={setRecipient} prefix="wa-recipient" showReferences />
            </CardContent>
          </Card>

          {/* Paquete */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Box className="w-5 h-5 text-violet-600" />
                Datos del paquete
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Contenido del paquete *</Label>
                <Textarea placeholder="Ej: Ropa, electrónicos, documentos..."
                  value={pkg.content} onChange={(e) => setPkg({ ...pkg, content: e.target.value })}
                  rows={2} className="mt-1" data-testid="wa-pkg-content" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Peso (kg) *</Label>
                  <Input type="number" placeholder="2.5" min="0.1" step="0.1"
                    value={pkg.weight} onChange={(e) => setPkg({ ...pkg, weight: e.target.value })}
                    className="mt-1" data-testid="wa-pkg-weight" />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label>Dimensiones (cm)</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    <Input type="number" placeholder="Largo" min="1"
                      value={pkg.length} onChange={(e) => setPkg({ ...pkg, length: e.target.value })}
                      data-testid="wa-pkg-length" />
                    <Input type="number" placeholder="Ancho" min="1"
                      value={pkg.width} onChange={(e) => setPkg({ ...pkg, width: e.target.value })}
                      data-testid="wa-pkg-width" />
                    <Input type="number" placeholder="Alto" min="1"
                      value={pkg.height} onChange={(e) => setPkg({ ...pkg, height: e.target.value })}
                      data-testid="wa-pkg-height" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button type="submit"
            className="w-full py-7 text-lg font-bold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-xl shadow-green-500/30 rounded-xl"
            data-testid="wa-submit-btn">
            <MessageCircle className="w-6 h-6 mr-2" />
            Enviar solicitud por WhatsApp
          </Button>

          <p className="text-center text-xs text-zinc-400">
            Al enviar, se abrirá WhatsApp con todos tus datos para que un agente genere tu guía.
          </p>
        </form>
      </div>
    </div>
  );
};

// Simplified address form for WhatsApp flow
const WhatsAppAddressForm = ({ data, setData, prefix, showReferences = false }) => {
  const [colonias, setColonias] = useState([]);
  const [showColoniaSearch, setShowColoniaSearch] = useState(false);
  const [manualColonia, setManualColonia] = useState(false);
  const update = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  const fetchColonias = async (val) => {
    if (val.length === 5) {
      try {
        const res = await axios.get(`${API}/zipcode/${val}`);
        if (res.data.colonias?.length > 0) {
          setColonias(res.data.colonias);
          update("state", res.data.estado || "");
          update("city", res.data.ciudad || res.data.estado || "");
          if (res.data.colonias.length === 1) update("colonia", res.data.colonias[0]);
        } else {
          setColonias([]);
        }
      } catch { setColonias([]); }
    } else {
      setColonias([]);
    }
  };

  const handleZipChange = (val) => {
    update("zip", val);
    update("colonia", "");
    fetchColonias(val);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Código Postal *</Label>
          {showColoniaSearch ? (
            <div className="mt-1">
              <ColoniaSearch
                placeholder="Busca tu colonia..."
                onSelect={(item) => {
                  update("zip", item.cp);
                  update("colonia", item.colonia);
                  update("state", item.estado);
                  update("city", item.ciudad || item.municipio);
                  fetchColonias(item.cp);
                  setShowColoniaSearch(false);
                  toast.success(`CP ${item.cp} - ${item.colonia}`);
                }}
                onClose={() => setShowColoniaSearch(false)}
              />
            </div>
          ) : (
            <>
              <Input placeholder="06600" maxLength={5} value={data.zip}
                onChange={(e) => handleZipChange(e.target.value)} className="mt-1 h-11"
                data-testid={`${prefix}-zip`} />
              <button type="button" onClick={() => setShowColoniaSearch(true)}
                className="text-[11px] text-violet-600 hover:text-violet-800 mt-1 flex items-center gap-1 font-medium"
                data-testid={`${prefix}-search-colonia-btn`}>
                <Search className="w-3 h-3" /> ¿No sabes tu CP?
              </button>
            </>
          )}
        </div>
        <div>
          <Label>Estado *</Label>
          <select value={data.state} onChange={(e) => update("state", e.target.value)}
            className="w-full mt-1 h-11 px-3 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            data-testid={`${prefix}-state`}>
            <option value="">Selecciona estado</option>
            {ESTADOS_MEXICO.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Colonia *</Label>
          {colonias.length > 0 && !manualColonia ? (
            <>
              <select value={data.colonia} onChange={(e) => update("colonia", e.target.value)}
                className="w-full mt-1 h-11 px-3 border-2 border-violet-200 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-violet-500 bg-violet-50 text-violet-700"
                data-testid={`${prefix}-colonia-select`}>
                <option value="">Selecciona colonia</option>
                {colonias.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button type="button" onClick={() => { setManualColonia(true); update("colonia", ""); }}
                className="text-[11px] text-violet-600 hover:text-violet-800 mt-1 flex items-center gap-1 font-medium"
                data-testid={`${prefix}-manual-colonia-btn`}>
                ¿No encuentras tu colonia? Escríbela
              </button>
            </>
          ) : (
            <>
              <Input placeholder="Escribe tu colonia" value={data.colonia}
                onChange={(e) => update("colonia", e.target.value)} className="mt-1 h-11"
                data-testid={`${prefix}-colonia`} />
              {colonias.length > 0 && manualColonia && (
                <button type="button" onClick={() => { setManualColonia(false); update("colonia", ""); }}
                  className="text-[11px] text-violet-600 hover:text-violet-800 mt-1 flex items-center gap-1 font-medium">
                  Volver al listado
                </button>
              )}
            </>
          )}
        </div>
        <div>
          <Label>Ciudad *</Label>
          <Input placeholder="Ciudad de México" value={data.city}
            onChange={(e) => update("city", e.target.value)} className="mt-1 h-11"
            data-testid={`${prefix}-city`} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Nombre completo *</Label>
          <Input placeholder="Juan Pérez López" value={data.name}
            onChange={(e) => update("name", e.target.value)} className="mt-1 h-11"
            data-testid={`${prefix}-name`} />
        </div>
        <div>
          <Label>Teléfono *</Label>
          <Input placeholder="55 1234 5678" value={data.phone}
            onChange={(e) => update("phone", e.target.value)} className="mt-1 h-11"
            data-testid={`${prefix}-phone`} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Calle y número *</Label>
          <Input placeholder="Av. Insurgentes 1234" value={data.street}
            onChange={(e) => update("street", e.target.value)} className="mt-1 h-11"
            data-testid={`${prefix}-street`} />
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" placeholder="correo@ejemplo.com" value={data.email}
            onChange={(e) => update("email", e.target.value)} className="mt-1 h-11"
            data-testid={`${prefix}-email`} />
        </div>
      </div>
      {showReferences && (
        <div>
          <Label>Referencias (opcional)</Label>
          <Input placeholder="Entre calle X y Y, frente al parque" value={data.references || ""}
            onChange={(e) => update("references", e.target.value)} className="mt-1 h-11"
            data-testid={`${prefix}-references`} />
        </div>
      )}
    </div>
  );
};

export default SolicitarGuia;
